if (!customElements.get('collections-filtering')) {
	class CollectionsFiltering extends HTMLElement {
		constructor() {
			super();

			this.form = this.querySelector('form');
			this.button = this.querySelector('button[type="submit"]');
			this.select = this.querySelector('select.dropdown-collection');
			this.input = this.querySelector('input.input-collection');
			this.selects = this.querySelectorAll('select.dropdown-filter');

			this.chevronSvg = `
				<svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none" aria-hidden="true" focusable="false">
					<path d="M0.833374 0.833313L5.83337 5.83331L10.8334 0.833313" stroke="#4F4F4F" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
			`;

			this.setupEventListeners();
			this.enhanceAllSelects();

			// There's no collection dropdown so we either need to populate
			// the filters of the entire store or the single collection chosen
			// (if only one collection has been chosen).
			if (!this.select) {
				this.populateCollectionFilters(true);
			}
		}

		setupEventListeners() {
			this.form.addEventListener('submit', (event) => {
				event.preventDefault();
				window.location = this.form.getAttribute('action');
			});
			this.select?.addEventListener('change', this.onCollectionChange.bind(this));
			this.selects.forEach((event) => {
				event.addEventListener('change', this.onFilterChange.bind(this));
			});

			document.addEventListener('click', (event) => {
				if (!event.target.closest('.filters-dropdown')) {
					this.closeAllDropdowns();
				}
			});

			document.addEventListener('keydown', (event) => {
				if (event.key === 'Escape') {
					this.closeAllDropdowns();
				}
			});
		}

		enhanceAllSelects() {
			this.querySelectorAll('select.dropdown-collection, select.dropdown-filter').forEach((select) => {
				this.enhanceSelect(select);
			});
		}

		enhanceSelect(select) {
			if (select.closest('.filters-dropdown')) {
				this.syncDropdown(select);
				return;
			}

			const wrapper = document.createElement('div');
			wrapper.className = 'filters-dropdown';
			if (select.disabled) {
				wrapper.classList.add('is-disabled');
			}

			const trigger = document.createElement('button');
			trigger.type = 'button';
			trigger.className = 'filters-dropdown-trigger';
			trigger.setAttribute('aria-haspopup', 'listbox');
			trigger.setAttribute('aria-expanded', 'false');
			trigger.disabled = select.disabled;

			const label = document.createElement('span');
			label.className = 'filters-dropdown-label';
			label.textContent = this.getSelectLabel(select);

			const icon = document.createElement('span');
			icon.className = 'filters-dropdown-icon';
			icon.innerHTML = this.chevronSvg;

			trigger.append(label, icon);

			const list = document.createElement('ul');
			list.className = 'filters-dropdown-list';
			list.setAttribute('role', 'listbox');
			list.hidden = true;

			select.parentNode.insertBefore(wrapper, select);
			wrapper.append(trigger, list, select);
			select.classList.add('filters-dropdown-select');

			trigger.addEventListener('click', (event) => {
				event.preventDefault();
				if (select.disabled) return;
				const willOpen = list.hidden;
				this.closeAllDropdowns();
				if (willOpen) {
					this.openDropdown(wrapper);
				}
			});

			this.syncDropdown(select);

			select.addEventListener('change', () => {
				this.syncDropdown(select);
			});
		}

		getSelectLabel(select) {
			const selected = select.options[select.selectedIndex];
			if (selected) {
				return selected.textContent.trim();
			}
			const fallback = select.querySelector('option.default');
			return fallback ? fallback.textContent.trim() : '';
		}

		syncDropdown(select) {
			const wrapper = select.closest('.filters-dropdown');
			if (!wrapper) return;

			const trigger = wrapper.querySelector('.filters-dropdown-trigger');
			const label = wrapper.querySelector('.filters-dropdown-label');
			const list = wrapper.querySelector('.filters-dropdown-list');

			wrapper.classList.toggle('is-disabled', select.disabled);
			if (trigger) {
				trigger.disabled = select.disabled;
			}
			if (label) {
				label.textContent = this.getSelectLabel(select);
			}

			if (!list) return;

			list.innerHTML = '';
			Array.from(select.options).forEach((option) => {
				if (option.hidden) return;

				const item = document.createElement('li');
				item.className = 'filters-dropdown-option';
				item.setAttribute('role', 'option');
				item.dataset.value = option.value;
				item.textContent = option.textContent.trim();

				if (option.disabled && option.classList.contains('default') && option.value === '') {
					item.classList.add('is-placeholder');
				}

				if (option.selected) {
					item.classList.add('is-selected');
					item.setAttribute('aria-selected', 'true');
				}

				if (option.disabled && option.value !== '') {
					item.classList.add('is-disabled');
				}

				item.addEventListener('click', (event) => {
					event.preventDefault();
					if (option.disabled && option.value === '') {
						this.closeDropdown(wrapper);
						return;
					}
					if (option.disabled) return;

					select.value = option.value;
					Array.from(select.options).forEach((opt) => {
						opt.selected = opt === option;
					});
					select.dispatchEvent(new Event('change', { bubbles: true }));
					this.closeDropdown(wrapper);
				});

				list.appendChild(item);
			});
		}

		openDropdown(wrapper) {
			const trigger = wrapper.querySelector('.filters-dropdown-trigger');
			const list = wrapper.querySelector('.filters-dropdown-list');
			if (!trigger || !list || wrapper.classList.contains('is-disabled')) return;
			wrapper.classList.add('is-open');
			list.hidden = false;
			trigger.setAttribute('aria-expanded', 'true');
		}

		closeDropdown(wrapper) {
			const trigger = wrapper.querySelector('.filters-dropdown-trigger');
			const list = wrapper.querySelector('.filters-dropdown-list');
			if (!trigger || !list) return;
			wrapper.classList.remove('is-open');
			list.hidden = true;
			trigger.setAttribute('aria-expanded', 'false');
		}

		closeAllDropdowns() {
			this.querySelectorAll('.filters-dropdown.is-open').forEach((wrapper) => {
				this.closeDropdown(wrapper);
			});
		}

		constructURL() {
			const collectionUrl = this.select?.value ?? this.input?.value ?? '/collections/all/';

			const url = new URL(collectionUrl, window.shopUrl);

			this.querySelectorAll('select.dropdown-filter:enabled').forEach((select) => {
				if (select.value === "") {
					return;
				}

				Array.from(select.selectedOptions).forEach((option) => {
					url.searchParams.set(option.dataset.paramName, option.value);
				});
			});

			return url.toString();
		}

		setFormUrl(url, background) {
			this.form.setAttribute('action', url);

			// This operation happens on the background
			// where the user should not be notified of loading with spinners
			// etc.
			if (!background) {
				this.button.disabled = false;
			}
		}

		selectRemoveOptions(select) {
			const options = select.querySelectorAll('option:not(.default)');

			options.forEach((option) => {
				select.removeChild(option);
			});

			select.querySelector('option').selected = true;
			this.syncDropdown(select);
		}

		onFilterChange(event) {
			const selectedIndex = event.target.dataset.index;

			this.selects.forEach((select) => {
				if (Number(select.dataset.index) <= Number(selectedIndex)) {
					return;
				}

				this.selectRemoveOptions(select);
				select.disabled = true;
				select.closest('.form-group').classList.add('disabled');
				this.syncDropdown(select);
			});

			this.setFormUrl(this.constructURL());

			const url = new URL(this.constructURL());
			url.searchParams.set('view', 'do_not_use');
			this.toggleLoading();

			fetch(url)
				.then((response) => {
					if (!response.ok) {
						throw new Error(response.status);
					}

					return response.text();
				})
				.then((text) => {
					this.toggleLoading();
					const resultsMarkup = new DOMParser().parseFromString(text, 'text/html');

					this.selects.forEach((select) => {
						if (Number(select.dataset.index) <= Number(selectedIndex)) {
							return;
						}

						const options = resultsMarkup.querySelectorAll(`select[data-label="${select.dataset.label}"] option`);
						if (options.length > 0) {
							const availableOptions = Array.from(options).filter(option => Number(option.dataset.count) > 0);
							select.append(...availableOptions);
							select.querySelector('option.default').selected = true;
						}

						if (Number(select.dataset.index) === Number(selectedIndex) + 1) {
							select.disabled = false;
							select.closest('.form-group').classList.remove('disabled');
						}

						this.syncDropdown(select);
					});
				})
				.catch((error) => {
					throw error;
				});
		}

		populateCollectionFilters(background = false) {
			this.setFormUrl(this.constructURL(), background);

			const url = new URL(this.constructURL());
			url.searchParams.set('view', 'do_not_use');

			// When we initially populate the filters we don't want
			// to show a loading spinner.
			if (!background) {
				this.toggleLoading();
			}

			fetch(url)
				.then((response) => {
					if (!response.ok) {
						throw new Error(response.status);
					}

					return response.text();
				})
				.then((text) => {
					if (!background) {
						this.toggleLoading();
					}

					const resultsMarkup = new DOMParser().parseFromString(text, 'text/html');

					this.selects.forEach((select) => {
						const options = resultsMarkup.querySelectorAll(`select[data-label="${select.dataset.label}"] option`);
						if (options.length > 0) {
							select.append(...options);
							select.querySelector('option.default').selected = true;
						}

						if (Number(select.dataset.index) === 1) {
							select.disabled = false;
							select.closest('.form-group').classList.remove('disabled');
						}

						this.syncDropdown(select);
					});
				})
				.catch((error) => {
					throw error;
				});
		}

		onCollectionChange() {
			this.selects.forEach((select) => {
				this.selectRemoveOptions(select);
				select.disabled = true;
				select.closest('.form-group').classList.add('disabled');
				this.syncDropdown(select);
			});

			this.populateCollectionFilters();
			this.syncDropdown(this.select);
		}

		toggleLoading() {
			this.classList.toggle('is-loading');
			this.button.classList.toggle('loading');
		}
	}

	customElements.define('collections-filtering', CollectionsFiltering);
}
